import { useQuery } from "@tanstack/react-query";
import { AbstractBaseEntityApi } from "./BaseEntityApi";
import {
  IRelationModel,
  IGenericRequestParameters,
  IEntityMinimalModel,
  IModelWithIntId,
  INamedEntity,
  IPaginationParameters,
  IPermissionedEntity,
  IRelatedEntity,
  IRelation,
  IUniqueEntity,
  EntityConstants,
  ICreatedRecordParameters,
  IModifiedRecordParameters,
  IEntryRecord,
  IModificationRecord,
  EntryRecordOrderBy,
  ModificationRecordOrderBy,
  GenericEntityOrderBy,
  ICustomTypedEntity,
  TypedEntitySuggestionsModel,
  INamedEntityParameters,
  ITypedEntityParameters,
  ILockableEntity,
  ILockableEntityParameters,
  NamedEntityOrderBy,
  TypedEntityOrderBy,
} from "./GenericTypes";
import { ProjectPersonPermission } from "../ProjectPersonPermissions/ProjectPersonPermissions";
import {
  IEntryRecordParametersTranslator,
  IGenericRequestParametersTranslator,
  IModifiedRecordParametersTranslator,
  IPaginationParametersTranslator,
  PropertyTranslator,
  INamedEntityParametersTranslator,
  IEntryRecordLabels,
  ICustomTypedEntityLabels,
  IModificationRecordLabels,
  IPermissionedEntityLabels,
  INamedEntityLabels,
  IUniqueEntityLabels,
  IRelatedEntityLabels,
  ITypedEntityParametersTranslator,
  ILockableEntityParametersTranslator,
  ILockableEntityLabels,
} from "./GenericTranslator";

export const projectsConstants: EntityConstants<Project, ProjectFilters> = {
  resource: "projects",
  frontendIndexRoute: "projects",
  entitySingular: "project",
  entityPlural: "projects",
  icon: "folder",
};

export const ProjectFieldLabels: PropertyTranslator<Project> = {
  ...INamedEntityLabels,
  ...IPermissionedEntityLabels,
  ...IEntryRecordLabels,
  ...IModificationRecordLabels,
  ...ICustomTypedEntityLabels,
  ...IUniqueEntityLabels,
  ...IRelatedEntityLabels,
  ...ILockableEntityLabels,
  id: "Project ID",
  projectPersonPermissions: "Project access",
} as const;

export interface ProjectRelations extends IRelationModel {
  datasets: IRelation;
  inventoryItems: IRelation;
  labNotebooks: IRelation;
  samples: IRelation;
}

export interface Project
  extends IPermissionedEntity,
    IModelWithIntId,
    INamedEntity,
    IRelatedEntity<ProjectRelations>,
    IUniqueEntity,
    IEntryRecord,
    IModificationRecord,
    ICustomTypedEntity,
    ILockableEntity {
  projectPersonPermissions?: ProjectPersonPermission[];
}
export interface ProjectSuggestions extends TypedEntitySuggestionsModel<Project["id"]> {}

export interface ProjectFilters
  extends IGenericRequestParameters<Project, ProjectSortingOptions>,
    IPaginationParameters,
    ICreatedRecordParameters,
    IModifiedRecordParameters,
    INamedEntityParameters,
    ITypedEntityParameters,
    ILockableEntityParameters {
  personIds?: number[] | null;
  datasetIds?: number[] | null;
  inventoryIds?: number[] | null;
  labNotebookIds?: number[] | null;
  sampleIds?: number[] | null;
  currentUserHasAddPermission?: boolean | null;
}
export const ProjectFiltersTranslator: PropertyTranslator<ProjectFilters> = {
  ...IGenericRequestParametersTranslator,
  ...IPaginationParametersTranslator,
  ...IEntryRecordParametersTranslator,
  ...IModifiedRecordParametersTranslator,
  ...ITypedEntityParametersTranslator,
  ...INamedEntityParametersTranslator,
  ...ILockableEntityParametersTranslator,
  personIds: "Person IDs",
  datasetIds: "Dataset IDs",
  inventoryIds: "Inventory item IDs",
  labNotebookIds: "Lab notebook IDs",
  sampleIds: "Sample IDs",
  currentUserHasAddPermission: "Current user has add permission",
} as const;

export const ProjectSortingOptionsConsts = [
  ...GenericEntityOrderBy,
  ...NamedEntityOrderBy,
  ...EntryRecordOrderBy,
  ...ModificationRecordOrderBy,
  ...TypedEntityOrderBy,
  "DATASET_COUNT",
  "INVENTORY_ITEM_COUNT",
  "LAB_NOTEBOOK_COUNT",
  "SAMPLE_COUNT",
] as const;
export type ProjectSortingOptions = (typeof ProjectSortingOptionsConsts)[number];

export const ProjectSearchTermOptions = {
  searchTermIncludeNames: ProjectFiltersTranslator["searchTermIncludeNames"],
  searchTermIncludeIds: ProjectFiltersTranslator["searchTermIncludeIds"],
  searchTermIncludeUuids: ProjectFiltersTranslator["searchTermIncludeUuids"],
  searchTermIncludeCustomFieldValues: ProjectFiltersTranslator["searchTermIncludeCustomFieldValues"],
} as const;

export class ProjectsApi extends AbstractBaseEntityApi<Project> {
  suggestions = async (): Promise<IEntityMinimalModel[]> => await this.api.get("projects/suggestions");
  useSuggestions = () => () => useQuery([this.resource, "list", "suggestions"], this.suggestions);
}
